import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppRoutes } from '../../../interfaces/store/appStore'
import { useAppHandlers } from '../../../services/actions/app/useAppHandlers'
import { useAuthHandlers } from '../../../services/actions/auth/useAuthHandlers'
import { useAppActions } from '../../../stores/application/useAppActions'

/**
 * On extension-install onboarding: when the user returns to this tab, detect the
 * Chrome extension and advance immediately (user refresh runs in the background).
 */
export const useOnboardingExtensionInstallDetect = (enabled = true) => {
    const { checkExtensionStatus } = useAppHandlers()
    const { getUserHandler } = useAuthHandlers()
    const { setOnboardingStep } = useAppActions()
    const navigate = useNavigate()
    const advancingRef = useRef(false)
    const checkInFlightRef = useRef(false)

    const tryAdvanceIfExtensionInstalled = useCallback(async () => {
        if (!enabled || advancingRef.current || checkInFlightRef.current) return
        if (document.visibilityState !== 'visible') return

        checkInFlightRef.current = true
        try {
            const installed = await checkExtensionStatus()
            if (!installed || advancingRef.current) return

            advancingRef.current = true
            await getUserHandler(undefined, true)
            setOnboardingStep(2)
            navigate(AppRoutes.ONBOARDING_LINKEDIN, { replace: true })
        } finally {
            checkInFlightRef.current = false
        }
    }, [enabled, checkExtensionStatus, getUserHandler, navigate, setOnboardingStep])

    useEffect(() => {
        if (!enabled) return

        const onTabActivated = () => {
            void tryAdvanceIfExtensionInstalled()
        }

        window.addEventListener('focus', onTabActivated)
        document.addEventListener('visibilitychange', onTabActivated)

        return () => {
            window.removeEventListener('focus', onTabActivated)
            document.removeEventListener('visibilitychange', onTabActivated)
        }
    }, [enabled, tryAdvanceIfExtensionInstalled])

    return { tryAdvanceIfExtensionInstalled }
}
